export type InteractiveToolCallLike = {
  toolCallId?: string;
  toolName?: string;
  args?: unknown;
  result?: unknown;
};

export type InteractiveToolMessageLike = {
  role?: string;
  toolCallId?: string;
  toolName?: string;
  content?: string;
  interactiveToolCalls?: unknown;
};

const getInteractiveToolCallId = (
  message: InteractiveToolMessageLike,
): string | undefined => {
  if (!Array.isArray(message.interactiveToolCalls)) {
    return undefined;
  }

  const first = message.interactiveToolCalls[0] as
    | InteractiveToolCallLike
    | undefined;
  return typeof first?.toolCallId === "string" ? first.toolCallId : undefined;
};

const getInteractiveToolCall = (
  message: InteractiveToolMessageLike,
): InteractiveToolCallLike | undefined => {
  if (!Array.isArray(message.interactiveToolCalls)) {
    return undefined;
  }

  return message.interactiveToolCalls[0] as InteractiveToolCallLike | undefined;
};

const hasPersistedInteractiveToolResult = (result: unknown): boolean => {
  if (result == null) {
    return false;
  }

  if (typeof result === "string") {
    return result.trim().length > 0;
  }

  return true;
};

const getMatchingNextToolMessage = (
  message: InteractiveToolMessageLike,
  nextMessage?: InteractiveToolMessageLike,
): InteractiveToolMessageLike | undefined => {
  const toolCallId = getInteractiveToolCallId(message);
  if (!toolCallId || !nextMessage || nextMessage.role !== "tool") {
    return undefined;
  }

  return nextMessage.toolCallId === toolCallId ? nextMessage : undefined;
};

const getMatchingPrevAssistantMessage = (
  message: InteractiveToolMessageLike,
  prevMessage?: InteractiveToolMessageLike,
): InteractiveToolMessageLike | undefined => {
  if (message.role !== "tool" || !message.toolCallId || !prevMessage) {
    return undefined;
  }

  const prevToolCallId = getInteractiveToolCallId(prevMessage);
  return prevToolCallId === message.toolCallId ? prevMessage : undefined;
};

/**
 * 将 assistant.interactiveToolCalls[0] 与下一条同 toolCallId 的 role:tool 合并。
 * result 优先取接口已持久化的 interactiveToolCalls[0].result，流式阶段回落到 tool.content。
 */
export const resolveMergedInteractiveToolCall = (
  message: InteractiveToolMessageLike,
  nextMessage?: InteractiveToolMessageLike,
): InteractiveToolCallLike | undefined => {
  if (message.role === "tool") {
    if (!message.toolName || !message.toolCallId) {
      return undefined;
    }

    return {
      toolCallId: message.toolCallId,
      toolName: message.toolName,
      result: message.content,
    };
  }

  const base = getInteractiveToolCall(message);
  if (!base?.toolCallId) {
    return undefined;
  }

  const matchingTool = getMatchingNextToolMessage(message, nextMessage);
  const mergedResult = hasPersistedInteractiveToolResult(base.result)
    ? base.result
    : matchingTool?.content;

  return {
    ...base,
    toolName: base.toolName ?? matchingTool?.toolName,
    result: mergedResult,
  };
};

/**
 * role:tool 已合并进上一条 assistant 时，跳过 tool 气泡渲染（数据层仍保留）。
 */
export const shouldSkipMergedToolMessageRender = (
  message: InteractiveToolMessageLike,
  prevMessage?: InteractiveToolMessageLike,
): boolean => {
  return getMatchingPrevAssistantMessage(message, prevMessage) != null;
};

export type MergeAndFilterInteractiveToolMessagesResult<T> = {
  messages: T[];
  messageIds: (string | undefined)[];
};

/**
 * 按 toolCallId 将 role:tool.content 合并进上一条 assistant.interactiveToolCalls[0].result，
 * 并从展示列表过滤已合并的 tool 消息（原始 messages 不变，仅用于渲染层）。
 */
export const mergeAndFilterInteractiveToolMessages = <
  T extends InteractiveToolMessageLike,
>(
  messages: T[],
  messageIds: (string | undefined)[] = [],
): MergeAndFilterInteractiveToolMessagesResult<T> => {
  if (!messages.length) {
    return { messages, messageIds };
  }

  const nextMessages: T[] = [];
  const nextMessageIds: (string | undefined)[] = [];

  messages.forEach((item, index) => {
    const prev = messages[index - 1];

    if (shouldSkipMergedToolMessageRender(item, prev)) {
      return;
    }

    const next = messages[index + 1];
    const mergedToolCall = resolveMergedInteractiveToolCall(item, next);

    if (
      item.role !== "tool" &&
      mergedToolCall &&
      Array.isArray(item.interactiveToolCalls)
    ) {
      const calls = item.interactiveToolCalls as InteractiveToolCallLike[];
      nextMessages.push({
        ...item,
        interactiveToolCalls: [mergedToolCall, ...calls.slice(1)],
      } as T);
      nextMessageIds.push(messageIds[index]);
      return;
    }

    nextMessages.push(item);
    nextMessageIds.push(messageIds[index]);
  });

  return { messages: nextMessages, messageIds: nextMessageIds };
};
